import { gql } from "@apollo/client";

export const GET_COMMISSION_STATEMENTS = gql`
    query GetCommissionStatements($pageIndex: Int!, $pageSize: Int!, $orderAsc: Boolean!, $orderField: String!, $filters: CommissionStatementFilterInput) {
        salesCommissionStatements(pagedInput: {
            pageInfo: {
                pageIndex: $pageIndex,
                pageSize: $pageSize
            },
            orderAsc: $orderAsc,
            orderField: $orderField
        }, filters: $filters) {
            items {
                id
                statementNumber
                agentId {
                    id
                    firstName
                    lastName
                    email
                }
                periodStart
                periodEnd
                periodDescription
                totalDeals
                totalSellingValue
                totalGrossCommission
                totalVATAmount
                totalReferralDeductions
                totalNetCommission
                totalAdjustments
                finalCommissionAmount
                status
                generatedDate
                sentDate
                paidDate
                paymentInfo {
                    paymentDue
                    paymentReceived
                    paymentDate
                    paymentReference
                    paymentMethod
                }
                createdAt
                updatedAt
            }
            pageInfo {
                totalCount
                totalPages
                pageSize
                pageIndex
                hasNextPage
                hasPreviousPage
            }
        }
    }
`;

export const GET_COMMISSION_STATEMENT = gql`
    query GetCommissionStatement($id: ID!) {
        salesCommissionStatement(id: $id) {
            id
            statementNumber
            agentId {
                id
                firstName
                lastName
                email
                primaryPhone
                bankingDetailsInfo {
                    bankName
                    accountNumber
                    branchCode
                    accountHolder
                }
            }
            periodStart
            periodEnd
            periodDescription
            deals {
                dealId {
                    id
                    dealNumber
                    streetAddress
                }
                dealNumber
                propertyAddress
                sellingPrice
                commissionPercentage
                agentPercentage
                grossCommission
                vatAmount
                referralDeduction
                netCommission
                dateCompleted
            }
            totalDeals
            totalSellingValue
            totalGrossCommission
            totalVATAmount
            totalReferralDeductions
            totalNetCommission
            adjustments {
                id
                description
                amount
                type
                reason
                date
            }
            totalAdjustments
            finalCommissionAmount
            status
            generatedDate
            sentDate
            paidDate
            paymentInfo {
                paymentDue
                paymentReceived
                paymentDate
                paymentReference
                paymentMethod
                bankDetails {
                    bankName
                    accountNumber
                    branchCode
                    accountHolder
                }
            }
            notes
            createdAt
            updatedAt
            createdBy {
                id
                firstName
                lastName
            }
            updatedBy {
                id
                firstName
                lastName
            }
        }
    }
`;

export const GET_STATEMENTS_BY_AGENT = gql`
    query GetStatementsByAgent($agentId: ID!, $pageIndex: Int!, $pageSize: Int!) {
        commissionStatementsByAgent(agentId: $agentId, pagedInput: {
            pageInfo: {
                pageIndex: $pageIndex,
                pageSize: $pageSize
            }
        }) {
            items {
                id
                statementNumber
                periodStart
                periodEnd
                periodDescription
                totalDeals
                totalNetCommission
                finalCommissionAmount
                status
                generatedDate
                sentDate
                paidDate
                paymentInfo {
                    paymentReceived
                    paymentDate
                }
            }
            pageInfo {
                totalCount
                totalPages
                pageSize
                pageIndex
                hasNextPage
                hasPreviousPage
            }
        }
    }
`;

export const GET_STATEMENTS_REQUIRING_ATTENTION = gql`
    query GetStatementsRequiringAttention {
        statementsRequiringAttention {
            id
            statementNumber
            agentId {
                id
                firstName
                lastName
            }
            periodStart
            periodEnd
            totalNetCommission
            status
            generatedDate
            sentDate
            paymentInfo {
                paymentDue
                paymentReceived
            }
        }
    }
`;

export const GET_COMMISSION_STATISTICS = gql`
    query GetCommissionStatistics($startDate: Date, $endDate: Date) {
        commissionStatistics(startDate: $startDate, endDate: $endDate) {
            statusDistribution {
                status
                count
                totalCommission
                averageCommission
            }
            agentStatistics {
                agentId {
                    id
                    firstName
                    lastName
                }
                statements
                totalCommission
                averageCommission
                totalDeals
            }
            monthlyTrends {
                year
                month
                statements
                totalCommission
                totalDeals
            }
            overallStats {
                totalStatements
                totalCommission
                averageCommission
                totalDeals
                averageDealsPerStatement
            }
        }
    }
`;

export const GET_UNPAID_STATEMENTS_SUMMARY = gql`
    query GetUnpaidStatementsSummary {
        unpaidStatementsSummary {
            count
            totalOutstanding
            oldestStatementDate
        }
    }
`;

export const SEARCH_COMMISSION_STATEMENTS = gql`
    query SearchCommissionStatements($searchTerm: String!, $pageIndex: Int!, $pageSize: Int!) {
        searchCommissionStatements(searchTerm: $searchTerm, pagedInput: {
            pageInfo: {
                pageIndex: $pageIndex,
                pageSize: $pageSize
            }
        }) {
            items {
                id
                statementNumber
                agentId {
                    id
                    firstName
                    lastName
                }
                periodStart
                periodEnd
                totalNetCommission
                status
                generatedDate
                sentDate
                paidDate
            }
            pageInfo {
                totalCount
                totalPages
                pageSize
                pageIndex
                hasNextPage
                hasPreviousPage
            }
        }
    }
`;

export const CREATE_COMMISSION_STATEMENT = gql`
    mutation CreateCommissionStatement($inputData: SalesCommissionStatementInput!) {
        createSalesCommissionStatement(inputData: $inputData) {
            id
            statementNumber
            agentId {
                id
                firstName
                lastName
            }
            periodStart
            periodEnd
            totalDeals
            totalNetCommission
            status
            createdAt
        }
    }
`;

export const GENERATE_COMMISSION_STATEMENT = gql`
    mutation GenerateCommissionStatement($inputData: GenerateStatementInput!) {
        generateCommissionStatement(inputData: $inputData) {
            id
            statementNumber
            agentId {
                id
                firstName
                lastName
            }
            periodStart
            periodEnd
            totalDeals
            totalSellingValue
            totalGrossCommission
            totalNetCommission
            status
            deals {
                dealNumber
                propertyAddress
                sellingPrice
                grossCommission
                netCommission
                dateCompleted
            }
            createdAt
        }
    }
`;

export const GENERATE_MULTIPLE_STATEMENTS = gql`
    mutation GenerateMultipleStatements($inputData: BulkStatementGenerationInput!) {
        generateCommissionStatements(inputData: $inputData) {
            agentId {
                id
                firstName
                lastName
            }
            success
            statementId
            message
        }
    }
`;

export const UPDATE_COMMISSION_STATEMENT = gql`
    mutation UpdateCommissionStatement($id: ID!, $inputData: SalesCommissionStatementInput!) {
        updateSalesCommissionStatement(id: $id, inputData: $inputData) {
            id
            statementNumber
            periodStart
            periodEnd
            totalDeals
            totalNetCommission
            status
            notes
            updatedAt
        }
    }
`;

export const UPDATE_STATEMENT_STATUS = gql`
    mutation UpdateStatementStatus($id: ID!, $status: CommissionStatementStatus!, $statusData: String) {
        updateCommissionStatementStatus(id: $id, status: $status, statusData: $statusData) {
            id
            status
            generatedDate
            sentDate
            paidDate
            paymentInfo {
                paymentDue
                paymentReceived
                paymentDate
                paymentReference
            }
            updatedAt
        }
    }
`;

export const MARK_STATEMENT_AS_GENERATED = gql`
    mutation MarkStatementAsGenerated($id: ID!) {
        markStatementAsGenerated(id: $id) {
            id
            status
            generatedDate
            updatedAt
        }
    }
`;

export const MARK_STATEMENT_AS_SENT = gql`
    mutation MarkStatementAsSent($id: ID!, $inputData: MarkAsSentInput) {
        markStatementAsSent(id: $id, inputData: $inputData) {
            id
            status
            sentDate
            paymentInfo {
                paymentDue
            }
            updatedAt
        }
    }
`;

export const MARK_STATEMENT_AS_PAID = gql`
    mutation MarkStatementAsPaid($id: ID!, $inputData: MarkAsPaidInput!) {
        markStatementAsPaid(id: $id, inputData: $inputData) {
            id
            status
            paidDate
            paymentInfo {
                paymentReceived
                paymentDate
                paymentReference
                paymentMethod
            }
            updatedAt
        }
    }
`;

export const ADD_COMMISSION_ADJUSTMENT = gql`
    mutation AddCommissionAdjustment($statementId: ID!, $adjustmentData: CommissionAdjustmentInput!) {
        addCommissionAdjustment(statementId: $statementId, adjustmentData: $adjustmentData) {
            id
            adjustments {
                id
                description
                amount
                type
                reason
                date
            }
            totalAdjustments
            finalCommissionAmount
            updatedAt
        }
    }
`;

export const REMOVE_COMMISSION_ADJUSTMENT = gql`
    mutation RemoveCommissionAdjustment($statementId: ID!, $adjustmentIndex: Int!) {
        removeCommissionAdjustment(statementId: $statementId, adjustmentIndex: $adjustmentIndex) {
            id
            adjustments {
                id
                description
                amount
                type
                reason
                date
            }
            totalAdjustments
            finalCommissionAmount
            updatedAt
        }
    }
`;

export const BULK_MARK_AS_SENT = gql`
    mutation BulkMarkStatementsAsSent($statementIds: [ID!]!, $inputData: MarkAsSentInput) {
        bulkMarkStatementsAsSent(statementIds: $statementIds, inputData: $inputData) {
            id
            status
            sentDate
            paymentInfo {
                paymentDue
            }
        }
    }
`;

export const BULK_MARK_AS_PAID = gql`
    mutation BulkMarkStatementsAsPaid($statementIds: [ID!]!, $inputData: MarkAsPaidInput!) {
        bulkMarkStatementsAsPaid(statementIds: $statementIds, inputData: $inputData) {
            id
            status
            paidDate
            paymentInfo {
                paymentReceived
                paymentDate
                paymentReference
            }
        }
    }
`;

export const DELETE_COMMISSION_STATEMENT = gql`
    mutation DeleteCommissionStatement($id: ID!) {
        deleteSalesCommissionStatement(id: $id)
    }
`;